import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Building2 } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { useToast } from '../../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreateInvoiceModal = ({ isOpen, onClose, onSuccess }) => {
  const [mode, setMode] = useState('manual'); // 'manual' or 'from_po'
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [pos, setPos] = useState([]);
  const [companySnapshot, setCompanySnapshot] = useState(null);
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');

  const [formData, setFormData] = useState({
    company_id: '',
    supplier_id: '',
    po_id: '',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    currency: 'USD',
    notes: '',
    terms: 'Payment due within 30 days',
    // Payment methods offered on this invoice
    payment_methods: ['bank_transfer'],
    payment_link_url: '',
    payment_link_provider: 'Network International',
    payment_link_label: '',
    items: [
      {
        product_name: '',
        description: '',
        quantity: 1,
        unit: 'units',
        unit_price: 0,
        tax_rate: 0,
        discount: 0
      }
    ]
  });

  useEffect(() => {
    if (isOpen) {
      fetchCompanies();
      fetchSuppliers();
      if (mode === 'from_po') {
        fetchPOs();
      }
    }
  }, [isOpen, mode]);

  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${API_URL}/api/procurement/companies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch companies');
      const data = await response.json();
      setCompanies(data);
      
      // Auto-select first company if only one exists
      if (data.length === 1) {
        setFormData({ ...formData, company_id: data[0].id });
        loadCompanySnapshot(data[0].id);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const loadCompanySnapshot = async (companyId) => {
    try {
      const response = await fetch(`${API_URL}/api/procurement/companies/${companyId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load company');
      const company = await response.json();
      
      setCompanySnapshot(company);
      
      // UAE-only: Default currency is AED
      // User can still choose other currencies (multi-currency preserved)
      const defaultCurrency = company.currency || 'AED';
      setFormData(prev => ({
        ...prev,
        currency: defaultCurrency
      }));
      
      console.log('Company snapshot loaded:', company.trading_name || company.legal_name);
      console.log('Default currency:', defaultCurrency);
    } catch (error) {
      console.error('Error loading company snapshot:', error);
      toast({
        title: 'Error',
        description: 'Failed to load company details',
        variant: 'destructive'
      });
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/procurement/suppliers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch suppliers');
      const data = await response.json();
      setSuppliers(data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const fetchPOs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/procurement/purchase-orders?status=approved`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch POs');
      const data = await response.json();
      setPos(data);
    } catch (error) {
      console.error('Error fetching POs:', error);
    }
  };

  const calculateLineItem = (item) => {
    const subtotal = item.quantity * item.unit_price;
    const tax_amount = subtotal * (item.tax_rate / 100);
    const discount_amount = subtotal * (item.discount / 100);
    const total = subtotal + tax_amount - discount_amount;

    return {
      ...item,
      subtotal,
      tax_amount,
      discount_amount,
      total
    };
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = {
      ...newItems[index],
      [field]: field === 'product_name' || field === 'description' || field === 'unit' ? value : parseFloat(value) || 0
    };
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        {
          product_name: '',
          description: '',
          quantity: 1,
          unit: 'units',
          unit_price: 0,
          tax_rate: 0,
          discount: 0
        }
      ]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData({ ...formData, items: newItems });
    }
  };

  const handleSubmitManual = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const calculatedItems = formData.items.map(calculateLineItem);

      const payload = {
        source_type: 'manual',
        company_id: formData.company_id,
        supplier_id: formData.supplier_id,
        issue_date: new Date(formData.issue_date).toISOString(),
        due_date: new Date(formData.due_date).toISOString(),
        currency: formData.currency,
        notes: formData.notes,
        terms: formData.terms,
        payment_methods: formData.payment_methods && formData.payment_methods.length
          ? formData.payment_methods
          : ['bank_transfer'],
        payment_link_url: formData.payment_methods?.includes('payment_link') ? (formData.payment_link_url || null) : null,
        payment_link_provider: formData.payment_methods?.includes('payment_link') ? (formData.payment_link_provider || 'Network International') : null,
        payment_link_label: formData.payment_methods?.includes('payment_link') ? (formData.payment_link_label || null) : null,
        items: calculatedItems
      };

      const response = await fetch(`${API_URL}/api/procurement/invoices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create invoice');
      }

      const data = await response.json();

      toast({
        title: 'Success',
        description: `Invoice ${data.invoice_number} created successfully`
      });

      onSuccess();
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create invoice',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFromPO = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Get selected PO to extract company_id
      const selectedPO = pos.find(po => po.id === formData.po_id);
      
      if (selectedPO && selectedPO.company_id) {
        // Auto-select company from PO for consistency
        await loadCompanySnapshot(selectedPO.company_id);
      }

      const response = await fetch(
        `${API_URL}/api/procurement/invoices/from-po/${formData.po_id}?issue_date=${formData.issue_date}&due_date=${formData.due_date}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create invoice from PO');
      }

      const data = await response.json();

      toast({
        title: 'Success',
        description: `Invoice ${data.invoice_number} created from PO successfully`
      });

      onSuccess();
    } catch (error) {
      console.error('Error creating invoice from PO:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create invoice from PO',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getTotals = () => {
    const calculatedItems = formData.items.map(calculateLineItem);
    const subtotal = calculatedItems.reduce((sum, item) => sum + item.subtotal, 0);
    const tax_total = calculatedItems.reduce((sum, item) => sum + item.tax_amount, 0);
    const discount_total = calculatedItems.reduce((sum, item) => sum + item.discount_amount, 0);
    const total = subtotal + tax_total - discount_total;
    return { subtotal, tax_total, discount_total, total };
  };

  const totals = getTotals();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-[#2B2D42]">Create Invoice</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          {/* Mode Toggle */}
          <div className="flex space-x-4 mb-6">
            <button
              onClick={() => setMode('manual')}
              className={`px-4 py-2 rounded-lg font-medium ${
                mode === 'manual'
                  ? 'bg-[#FF6B35] text-white'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              Manual Invoice
            </button>
            <button
              onClick={() => setMode('from_po')}
              className={`px-4 py-2 rounded-lg font-medium ${
                mode === 'from_po'
                  ? 'bg-[#FF6B35] text-white'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              From Purchase Order
            </button>
          </div>

          {mode === 'from_po' ? (
            <form onSubmit={handleSubmitFromPO} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                    Select Purchase Order *
                  </label>
                  <select
                    required
                    value={formData.po_id}
                    onChange={(e) => setFormData({ ...formData, po_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
                  >
                    <option value="">Select PO</option>
                    {pos.map((po) => (
                      <option key={po.id} value={po.id}>
                        {po.po_number} - {po.supplier_snapshot?.name} ({po.currency} {po.total_amount})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                    Issue Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.issue_date}
                    onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                    Due Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-[#FF6B35] hover:bg-[#E5562D]"
                >
                  {loading ? 'Creating...' : 'Create Invoice'}
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSubmitManual} className="space-y-6">
              {/* CRITICAL: Issuer Company Selection - MUST BE FIRST */}
              <div className="border-2 border-[#FF6B35] rounded-lg p-4 bg-[#FF6B35] bg-opacity-5">
                <div className="flex items-center space-x-2 mb-3">
                  <Building2 className="h-5 w-5 text-[#FF6B35]" />
                  <label className="block text-sm font-bold text-[#2B2D42]">
                    Issuer Company (Empresa Emissora) *
                  </label>
                </div>
                <select
                  required
                  value={formData.company_id}
                  onChange={(e) => {
                    const companyId = e.target.value;
                    setFormData({ ...formData, company_id: companyId });
                    if (companyId) {
                      loadCompanySnapshot(companyId);
                    }
                  }}
                  className="w-full px-3 py-2 border-2 border-[#FF6B35] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35] font-medium"
                >
                  <option value="">Select Issuer Company</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.trading_name || company.legal_name} ({company.country})
                    </option>
                  ))}
                </select>
                
                {/* Company Preview */}
                {companySnapshot && (
                  <div className="mt-3 p-3 bg-white rounded border border-gray-200">
                    <div className="text-xs space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[#6C757D]">Legal Name:</span>
                        <span className="font-medium text-[#2B2D42]">{companySnapshot.legal_name}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[#6C757D]">Country:</span>
                        <span className="font-medium text-[#2B2D42]">{companySnapshot.country}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[#6C757D]">Tax ID:</span>
                        <span className="font-medium text-[#2B2D42]">{companySnapshot.tax_id}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[#6C757D]">Default Currency:</span>
                        <span className="font-bold text-[#FF6B35]">{formData.currency}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                    Supplier (Recipient) *
                  </label>
                  <select
                    required
                    value={formData.supplier_id}
                    onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
                  >
                    <option value="">Select Supplier</option>
                    {suppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                    Issue Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.issue_date}
                    onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                    Due Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                    Currency *
                  </label>
                  <select
                    required
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="AED">AED</option>
                  </select>
                </div>
              </div>

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-[#2B2D42]">
                    Invoice Items *
                  </label>
                  <Button type="button" size="sm" onClick={addItem} variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                  </Button>
                </div>

                <div className="space-y-4">
                  {formData.items.map((item, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <span className="text-sm font-medium text-[#2B2D42]">Item {index + 1}</span>
                        {formData.items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="col-span-2">
                          <input
                            type="text"
                            placeholder="Product name *"
                            required
                            value={item.product_name}
                            onChange={(e) => handleItemChange(index, 'product_name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
                          />
                        </div>

                        <div>
                          <input
                            type="number"
                            placeholder="Quantity *"
                            required
                            min="0.01"
                            step="0.01"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
                          />
                        </div>

                        <div>
                          <input
                            type="number"
                            placeholder="Unit Price *"
                            required
                            min="0"
                            step="0.01"
                            value={item.unit_price}
                            onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
                          />
                        </div>

                        <div>
                          <input
                            type="number"
                            placeholder="Tax % (0-100)"
                            min="0"
                            max="100"
                            step="0.01"
                            value={item.tax_rate}
                            onChange={(e) => handleItemChange(index, 'tax_rate', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
                          />
                        </div>

                        <div>
                          <input
                            type="number"
                            placeholder="Discount % (0-100)"
                            min="0"
                            max="100"
                            step="0.01"
                            value={item.discount}
                            onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
                          />
                        </div>

                        <div className="col-span-2">
                          <input
                            type="text"
                            placeholder="Description (optional)"
                            value={item.description}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#6C757D]">Subtotal:</span>
                    <span className="font-medium text-[#2B2D42]">
                      {formData.currency} {totals.subtotal.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6C757D]">Tax:</span>
                    <span className="font-medium text-[#2B2D42]">
                      {formData.currency} {totals.tax_total.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6C757D]">Discount:</span>
                    <span className="font-medium text-[#2B2D42]">
                      {formData.currency} {totals.discount_total.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-gray-200">
                    <span className="font-bold text-[#2B2D42]">Total:</span>
                    <span className="font-bold text-lg text-[#FF6B35]">
                      {formData.currency} {totals.total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment options — Bank Transfer / Payment Link (Network International) */}
              <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-[11px] font-bold uppercase tracking-widest text-[#023E8A]">
                    Payment options offered to buyer
                  </label>
                  <span className="text-[10px] text-[#6C757D]">Shown on PDF &amp; email</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                  {[
                    { value: 'bank_transfer', label: 'Bank Transfer', hint: "Uses the company's default bank account snapshot." },
                    { value: 'payment_link', label: 'Payment Link (card / wallet)', hint: 'Hosted checkout URL — Network International, Stripe, PayPal.me…' },
                  ].map((opt) => {
                    const checked = (formData.payment_methods || []).includes(opt.value);
                    return (
                      <label
                        key={opt.value}
                        className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border cursor-pointer transition ${
                          checked ? 'border-[#023E8A] bg-white shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                        data-testid={`invoice-pm-${opt.value}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          className="mt-0.5 accent-[#023E8A]"
                          onChange={(e) => {
                            const set = new Set(formData.payment_methods || []);
                            if (e.target.checked) set.add(opt.value); else set.delete(opt.value);
                            const arr = Array.from(set);
                            setFormData({ ...formData, payment_methods: arr.length ? arr : ['bank_transfer'] });
                          }}
                        />
                        <span className="flex-1 min-w-0">
                          <span className="block text-[13px] font-bold text-[#2B2D42]">{opt.label}</span>
                          <span className="block text-[11px] text-[#6C757D] leading-snug">{opt.hint}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>

                {(formData.payment_methods || []).includes('payment_link') && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2">
                      <label className="block text-[11px] font-bold uppercase tracking-widest text-[#6C757D] mb-1">
                        Payment link URL *
                      </label>
                      <input
                        type="url"
                        value={formData.payment_link_url || ''}
                        onChange={(e) => setFormData({ ...formData, payment_link_url: e.target.value })}
                        placeholder="https://checkout.network.ae/…"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
                        data-testid="invoice-payment-link-url"
                      />
                      <div className="text-[10px] text-[#94a3b8] mt-1">
                        Paste the hosted checkout URL you generated at{' '}
                        <a href="https://www.network.ae/en/merchant-solutions/ecommerce-payments" target="_blank" rel="noreferrer" className="text-[#023E8A] underline">
                          network.ae Merchant Solutions
                        </a>{' '}
                        (or any provider — Stripe Payment Link, PayPal.me, etc.).
                      </div>
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-widest text-[#6C757D] mb-1">
                        Provider
                      </label>
                      <input
                        type="text"
                        value={formData.payment_link_provider || ''}
                        onChange={(e) => setFormData({ ...formData, payment_link_provider: e.target.value })}
                        placeholder="Network International"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        data-testid="invoice-payment-link-provider"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Notes & Terms */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                    Payment Terms
                  </label>
                  <textarea
                    value={formData.terms}
                    onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-[#FF6B35] hover:bg-[#E5562D]"
                >
                  {loading ? 'Creating...' : 'Create Invoice'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateInvoiceModal;
